import { cancelSession, useDeleteSession } from "@/features/chat/api/sessions";
import { useAbortSessionStreams } from "@/features/chat/streams/streamSelectors";
import { useAtom } from "jotai";
import { atomWithLazy } from "jotai/utils";
import { useCallback } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "paperclip.dataCuration.pendingChats";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** A new-curation chat whose agent has not created its curation yet. */
export interface PendingCurationChat {
  sessionId: string;
  startedAt: string;
}

const isPendingChat = (value: unknown): value is PendingCurationChat =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as PendingCurationChat).sessionId === "string" &&
  typeof (value as PendingCurationChat).startedAt === "string";

const readPending = (): PendingCurationChat[] => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - MAX_AGE_MS;
    return parsed
      .filter(isPendingChat)
      .filter((chat) => new Date(chat.startedAt).getTime() > cutoff);
  } catch {
    return [];
  }
};

const writePending = (chats: PendingCurationChat[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(chats));
  } catch {
    // Storage unavailable (private mode); the list still works for this visit.
  }
};

const pendingChatsAtom = atomWithLazy(readPending);

/**
 * New-curation chats still setting up, remembered across visits. The page and
 * the sidebar list share one copy, so a chat started on the page shows in the
 * sidebar at once.
 */
export const usePendingCurationChats = () => {
  const [pending, setPending] = useAtom(pendingChatsAtom);

  const addPending = useCallback(
    (sessionId: string) => {
      setPending((current) => {
        const next = [
          { sessionId, startedAt: new Date().toISOString() },
          ...current.filter((chat) => chat.sessionId !== sessionId),
        ];
        writePending(next);
        return next;
      });
    },
    [setPending],
  );

  const removePending = useCallback(
    (sessionIds: string[]) => {
      setPending((current) => {
        const next = current.filter((chat) => !sessionIds.includes(chat.sessionId));
        if (next.length === current.length) return current;
        writePending(next);
        return next;
      });
    },
    [setPending],
  );

  return { pending, addPending, removePending };
};

/**
 * A new curation still setting up has no curation to delete: stop its agent,
 * delete its chat, and drop it from the list.
 */
export const useDeletePendingCurationChat = () => {
  const { removePending } = usePendingCurationChats();
  const abortStreamsForSession = useAbortSessionStreams();
  const deleteSession = useDeleteSession();

  const deletePendingChat = async (sessionId: string, onDeleted?: () => void) => {
    abortStreamsForSession(sessionId);
    await cancelSession(sessionId).catch(() => undefined);
    deleteSession.mutate(sessionId, {
      onSuccess: () => {
        removePending([sessionId]);
        onDeleted?.();
      },
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : "Could not delete this chat.",
        ),
    });
  };

  return { deletePendingChat, deleting: deleteSession.isPending };
};
